import { useAPIRoute } from './useAPIRoute';
import { useQuery } from 'react-query';
import { Model } from 'rwl-shared';

type UserContainer = {
    user: Model.PortfolioUser;
    lastFetchedAtMillis: number;
};

const SERIALIZED_USER_KEY = 'rwl:serialized:user';
const ONE_DAY_IN_MILLIS = 1000 * 60 * 60 * 24;
type UseFetchConfigParams = {
    accountId: string;
    userId: string | undefined;
};

export const useFetchUser = ({ accountId, userId }: UseFetchConfigParams) => {
    const url = useAPIRoute('user');

    return useQuery<Model.PortfolioUser>(
        ['user', userId],
        async () => {
            if (!userId) {
                return undefined;
            }

            // look in localstorage first for the user
            const serializedUser =
                window.localStorage.getItem(SERIALIZED_USER_KEY);
            if (serializedUser) {
                const userContainer: UserContainer = JSON.parse(serializedUser);
                if (
                    userContainer.user.id === userId && // if they pass in a different user somehow, don't use the cached one
                    userContainer.lastFetchedAtMillis >
                        Date.now() - ONE_DAY_IN_MILLIS
                ) {
                    return userContainer.user;
                }
            }
            const result = await fetch(
                `${url}?` +
                    new URLSearchParams({
                        accountId: accountId,
                        userId: userId,
                    }),
                {
                    method: 'GET',
                }
            );
            const user = await result.json();
            // put the user in the localstorage
            const userContainer: UserContainer = {
                user: user,
                lastFetchedAtMillis: Date.now(),
            };
            window.localStorage.setItem(
                SERIALIZED_USER_KEY,
                JSON.stringify(userContainer)
            );
            return user;
        },
        {
            staleTime: 1000 * 60 * 60 * 24, // 24 hours
        }
    );
};
